import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../../components/Navbar";
import { BASE_URL } from "../../../const";

const URL = `${BASE_URL}/sku-requirement-summary/`;

export default function ManagementDashboard() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();
  const [warehouse, setWarehouse] = useState("pune");
  const [isAdmin, setIsAdmin] = useState(false);

  // Load data from localStorage on component mount
  useEffect(() => {
    const store = localStorage.getItem("store");

    // Check if user is admin
    if (store === "admin") {
      setIsAdmin(true);
      // Load saved warehouse preference for admin
      const savedWarehouse = localStorage.getItem("selectedWarehouse");
      if (savedWarehouse) {
        setWarehouse(savedWarehouse);
      }
    } else {
      // Set warehouse based on store for non-admin users
      if (store === "SELECT BRANDS (I) PVT. LTD. (PUNE)") {
        setWarehouse("pune");
      } else if (store === "SELECT BRANDS (I) PVT. LTD. (KOLKATA)") {
        setWarehouse("kolkata");
      }
    }

    localStorage.setItem("selectedWarehouse", warehouse)

  }, []);

  // Fetch data when warehouse changes
  useEffect(() => {
    if (warehouse) {
      loadData();
    }
    // intentionally only depend on warehouse
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouse]);

  const loadData = () => {
    const cacheKey = `skuRequirementData_${warehouse}`;
    const timestampKey = `skuRequirementTimestamp_${warehouse}`;

    const savedData = localStorage.getItem(cacheKey);


    if (savedData) {
      setData(JSON.parse(savedData));
      setLoading(false);
      return;
    }
    // no fresh cache, fetch from server
    fetchData();
  };

  // fetchData now uses query param for warehouse (GET cannot have a body)
  const fetchData = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const token = localStorage.getItem("token") || "";
      // attach warehouse as query param
      const fetchUrl = `${URL}?warehouse=${encodeURIComponent(warehouse)}`;

      const res = await fetch(fetchUrl, {
        method: "GET",
        headers: {
          Authorization: token ? `Token ${token}` : "",
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        // try to parse error message
        const txt = await res.text();
        console.error("API error:", res.status, txt);
        setData([]);
        return;
      }

      const json = await res.json();

      // Ensure data is an array (handle various backend shapes)
      const dataArray = Array.isArray(json)
        ? json
        : json.data || json.results || json.items || [];

      setData(dataArray);

      // Save to localStorage with warehouse-specific keys
      const cacheKey = `skuRequirementData_${warehouse}`;
      localStorage.setItem(cacheKey, JSON.stringify(dataArray));

    } catch (error) {
      console.error("Error fetching data:", error);
      setData([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    fetchData(true);
  };

  const handleManageTasks = () => {
    navigate("/task-section");
  };

  const handleWarehouseChange = (e) => {
    const newWarehouse = e.target.value;
    setWarehouse(newWarehouse);
    // Save warehouse preference for admin
    localStorage.setItem("selectedWarehouse", newWarehouse);
    loadData()
  };

  const clearCache = () => {
    const cacheKey = `skuRequirementData_${warehouse}`;
    const timestampKey = `skuRequirementTimestamp_${warehouse}`;
    localStorage.removeItem(cacheKey);
    localStorage.removeItem(timestampKey);
    fetchData();
  };

  const getDiffClass = (diff) => {
    if (diff > 0) return "text-red-600 bg-red-50";
    if (diff < 0) return "text-green-600 bg-green-50";
    return "text-gray-600";
  };

  return (
    <div className="p-2 sm:p-4 max-w-7xl mx-auto min-h-screen bg-gray-50">
      <Navbar title="Dashboard" />

      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            SKU Requirement Summary
          </h1>
          <p className="text-xs sm:text-sm text-gray-600 mt-1">
            Overview of inventory requirements across all stages
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
          {/* Warehouse Dropdown for Admin */}
          {isAdmin && (
            <select
              value={warehouse}
              onChange={handleWarehouseChange}
              className="px-3 py-2 sm:px-4 sm:py-2 bg-white border border-gray-300 rounded-md text-xs sm:text-sm text-gray-700 hover:bg-gray-50 transition-colors w-full sm:w-auto focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="pune">Pune Warehouse</option>
              <option value="kolkata">Kolkata Warehouse</option>
            </select>
          )}

          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center justify-center gap-2 px-3 py-2 sm:px-4 sm:py-2 bg-white border border-gray-300 rounded-md text-xs sm:text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 w-full sm:w-auto"
          >
            {refreshing ? (
              <>
                <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                Refreshing...
              </>
            ) : (
              <>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Refresh
              </>
            )}
          </button>

          <button
            onClick={handleManageTasks}
            className="px-3 py-2 sm:px-4 sm:py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium text-xs sm:text-sm w-full sm:w-auto"
          >
            Manage Tasks
          </button>
        </div>
      </div>

      {/* Main Card Wrapper */}
      <div className="bg-white rounded-lg shadow-sm border overflow-x-auto">
        {loading && !refreshing ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600 text-sm">Loading SKU data...</p>
          </div>
        ) : data.length === 0 ? (
          <div className="p-8 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 17v-2m0 0V9m0 8h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No data found</h3>
            <p className="mt-1 text-sm text-gray-500">
              No SKU requirement data available for {warehouse} warehouse.
            </p>
            <button
              onClick={handleRefresh}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-xs sm:text-sm">
            <thead className="bg-gray-50">
              {/* Main Header Row */}
              <tr>
                <th
                  rowSpan="2"
                  className="px-3 sm:px-4 py-3 text-left text-[10px] sm:text-xs font-semibold text-gray-700 uppercase bg-gray-100 border-r md:sticky md:left-0 md:z-20"
                >
                  SKU
                </th>
                <th
                  rowSpan="2"
                  className="px-3 sm:px-4 py-3 text-left text-[10px] sm:text-xs font-semibold text-gray-700 uppercase bg-gray-100 border-r md:sticky md:left-28 md:z-20"
                >
                  Plant Name
                </th>

                {/* Stage 3 Header */}
                <th
                  colSpan="3"
                  className="px-3 sm:px-4 py-3 text-center text-[10px] sm:text-xs font-semibold text-gray-700 uppercase bg-blue-50 border-l border-r"
                >
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                    Stage 3
                  </div>
                </th>

                {/* Stage 2 Header */}
                <th
                  colSpan="3"
                  className="px-3 sm:px-4 py-3 text-center text-[10px] sm:text-xs font-semibold text-gray-700 uppercase bg-green-50 border-l border-r"
                >
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-3 h-3 bg-green-600 rounded-full"></div>
                    Stage 2
                  </div>
                </th>

                {/* Stage 1 Header */}
                <th
                  colSpan="3"
                  className="px-3 sm:px-4 py-3 text-center text-[10px] sm:text-xs font-semibold text-gray-700 uppercase bg-purple-50 border-l border-r"
                >
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-3 h-3 bg-purple-600 rounded-full"></div>
                    Stage 1
                  </div>
                </th>
              </tr>

              {/* Sub-header Row */}
              <tr>
                {/* Stage 3 Sub-headers */}
                <th className="px-3 sm:px-4 py-2 text-center text-[10px] sm:text-xs font-medium text-gray-500 uppercase bg-blue-50 border-l">
                  Required
                </th>
                <th className="px-3 sm:px-4 py-2 text-center text-[10px] sm:text-xs font-medium text-gray-500 uppercase bg-blue-50">
                  Available
                </th>
                <th className="px-3 sm:px-4 py-2 text-center text-[10px] sm:text-xs font-medium text-gray-500 uppercase bg-blue-50 border-r">
                  Difference
                </th>

                {/* Stage 2 Sub-headers */}
                <th className="px-3 sm:px-4 py-2 text-center text-[10px] sm:text-xs font-medium text-gray-500 uppercase bg-green-50 border-l">
                  Required
                </th>
                <th className="px-3 sm:px-4 py-2 text-center text-[10px] sm:text-xs font-medium text-gray-500 uppercase bg-green-50">
                  Available
                </th>
                <th className="px-3 sm:px-4 py-2 text-center text-[10px] sm:text-xs font-medium text-gray-500 uppercase bg-green-50 border-r">
                  Difference
                </th>

                {/* Stage 1 Sub-headers */}
                <th className="px-3 sm:px-4 py-2 text-center text-[10px] sm:text-xs font-medium text-gray-500 uppercase bg-purple-50 border-l">
                  Required
                </th>
                <th className="px-3 sm:px-4 py-2 text-center text-[10px] sm:text-xs font-medium text-gray-500 uppercase bg-purple-50">
                  Available
                </th>
                <th className="px-3 sm:px-4 py-2 text-center text-[10px] sm:text-xs font-medium text-gray-500 uppercase bg-purple-50 border-r">
                  Difference
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200 bg-white">
              {Array.isArray(data) &&
                data.map((item, index) => {
                  const stage3 = item.stages?.find((s) => s.stage === 3) || {};
                  const stage2 = item.stages?.find((s) => s.stage === 2) || {};
                  const stage1 = item.stages?.find((s) => s.stage === 1) || {};

                  return (
                    <tr
                      key={item.sku || index}
                      className={`hover:bg-gray-50 transition-colors ${
                        index % 2 === 0 ? "bg-white" : "bg-gray-50"
                      }`}
                    >
                      {/* Fixed Columns (sticky only on md+) */}
                      <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm font-semibold text-gray-900 md:whitespace-nowrap md:sticky md:left-0 md:bg-white border-r md:z-10">
                        {item.sku}
                      </td>
                      <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm text-gray-700 md:whitespace-nowrap md:sticky md:left-28 md:bg-white border-r md:z-10">
                        {item.plant_name}
                      </td>

                      {/* Stage 3 Data */}
                      <td className="px-3 sm:px-4 py-3 text-center text-xs sm:text-sm text-gray-900 font-semibold border-l">
                        {stage3.qty_required ?? "-"}
                      </td>
                      <td className="px-3 sm:px-4 py-3 text-center text-xs sm:text-sm text-gray-800">
                        {stage3.qty_avl_in_batch ?? "-"}
                      </td>
                      <td
                        className={`px-3 sm:px-4 py-3 text-center text-xs sm:text-sm font-semibold border-r ${getDiffClass(
                          stage3.difference ?? 0
                        )}`}
                      >
                        {stage3.difference ?? "-"}
                      </td>

                      {/* Stage 2 Data */}
                      <td className="px-3 sm:px-4 py-3 text-center text-xs sm:text-sm text-gray-900 font-semibold border-l">
                        {stage2.qty_required ?? "-"}
                      </td>
                      <td className="px-3 sm:px-4 py-3 text-center text-xs sm:text-sm text-gray-800">
                        {stage2.qty_avl_in_batch ?? "-"}
                      </td>
                      <td
                        className={`px-3 sm:px-4 py-3 text-center text-xs sm:text-sm font-semibold border-r ${getDiffClass(
                          stage2.difference ?? 0
                        )}`}
                      >
                        {stage2.difference ?? "-"}
                      </td>

                      {/* Stage 1 Data */}
                      <td className="px-3 sm:px-4 py-3 text-center text-xs sm:text-sm text-gray-900 font-semibold border-l">
                        {stage1.qty_required ?? "-"}
                      </td>
                      <td className="px-3 sm:px-4 py-3 text-center text-xs sm:text-sm text-gray-800">
                        {stage1.qty_avl_in_batch ?? "-"}
                      </td>
                      <td
                        className={`px-3 sm:px-4 py-3 text-center text-xs sm:text-sm font-semibold border-r ${getDiffClass(
                          stage1.difference ?? 0
                        )}`}
                      >
                        {stage1.difference ?? "-"}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer Info */}
      {!loading && data.length > 0 && (
        <div className="mt-3 sm:mt-4 text-[10px] sm:text-xs text-gray-500 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span>
              {warehouse.charAt(0).toUpperCase() + warehouse.slice(1)} Warehouse • Data loaded from {refreshing ? "server" : "cache"} • Total records: {data.length}
            </span>
            <button onClick={clearCache} className="underline text-blue-600 hover:text-blue-700">
              Clear cache
            </button>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-50 border border-red-200 rounded"></div>
              <span>Shortage</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-green-50 border border-green-200 rounded"></div>
              <span>Surplus</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
